import { Button } from '@heroui/react'
import {
  useCreateCheckoutSession,
  useCreateFullPortalSession,
  useCreatePlanUpdatePortalSession,
  useCreateResumeSubscriptionPortalSession,
  useCreateSubscriptionCancellationPortal,
  usePlans,
} from '@qse/api'
import { UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { toast } from '@qse/ui'
import { usePageFocusRefetch } from '@qse/utils'
import { ArrowRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import PricingCardsContainer from '../../components/PricingCardsContainer'

const SubscriptionManagement = () => {
  const { user } = useUserStore()
  const {
    data: plansData,
    isLoading: isPlansLoading,
    error: plansError,
    refetch: refetchPlans,
  } = usePlans(undefined, {
    enabled: user?.userRole !== UserRoleEnum.TENANT_USER,
  })

  const createCheckoutSession = useCreateCheckoutSession()
  const createSubscriptionCancellationPortal = useCreateSubscriptionCancellationPortal()
  const createResumeSubscriptionPortalSession = useCreateResumeSubscriptionPortalSession()
  const createPlanUpdatePortalSession = useCreatePlanUpdatePortalSession()
  const createFullPortalSession = useCreateFullPortalSession()
  const [loadingPlang, setLoadingPlang] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [returnedFromPaymentLoading, setReturnedFromPaymentLoading] = useState(false)

  useEffect(() => {
    const returnedFromPayment =
      searchParams.get('plan-updated') === 'true' || searchParams.get('cancelled') === 'true'

    if (returnedFromPayment) {
      setReturnedFromPaymentLoading(true)
      searchParams.delete('plan-updated')
      searchParams.delete('cancelled')
      setSearchParams(searchParams)
      setTimeout(() => {
        refetchPlans()
        setReturnedFromPaymentLoading(false)
      }, 5000)
    }
  }, [refetchPlans, searchParams, setSearchParams])

  // Use the page focus refetch hook to automatically refetch plans data
  usePageFocusRefetch(() => {
    if (user?.userRole !== UserRoleEnum.TENANT_USER) {
      refetchPlans()
    }
  })

  const formatPrice = (amount: number): string => {
    const price = amount / 100 // Convert from cents

    return price.toFixed(0) // Remove decimals for clean display
  }

  const handleManageBilling = async (planId: string) => {
    try {
      setLoadingPlang(planId)
      const response = await createCheckoutSession.mutateAsync({
        planId,
      })

      if (response?.url) {
        // Open the portal URL in the same page
        window.location.href = response.url
      }
    } catch (error) {
      let errorMessage = 'Failed to create portal session'

      if (typeof error === 'string') {
        errorMessage = error
      } else if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message)
      }

      toast.error(errorMessage)
    } finally {
      setLoadingPlang(null)
    }
  }

  // Helper function to extract error message from backend response
  const getErrorMessage = (error: unknown): string => {
    if (!error || typeof error !== 'object') {
      return 'Unable to access billing management at this time. Please try again in a few moments or contact support if the issue persists.'
    }

    // Type guard for error with response property
    const errorWithResponse = error as {
      response?: {
        data?: {
          error?: { message?: string }
          message?: string
        }
      }
      message?: string
    }

    // Try different error message paths
    const errorMessage =
      errorWithResponse?.response?.data?.error?.message ||
      errorWithResponse?.response?.data?.message ||
      errorWithResponse?.message ||
      'Unable to access billing management at this time. Please try again in a few moments or contact support if the issue persists.'

    return errorMessage
  }

  const handleCancelSubscription = async (planId: string) => {
    try {
      setLoadingPlang(planId)
      const response = await createSubscriptionCancellationPortal.mutateAsync()

      if (response?.url) {
        window.location.href = response.url
      }
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setLoadingPlang(null)
    }
  }

  const handleResumeSubscription = async (planId: string) => {
    try {
      setLoadingPlang(planId)
      const response = await createResumeSubscriptionPortalSession.mutateAsync()

      if (response?.url) {
        window.location.href = response.url
      }
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setLoadingPlang(null)
    }
  }

  const handleUpgradePlan = async (planId: string) => {
    try {
      setLoadingPlang(planId)
      const response = await createPlanUpdatePortalSession.mutateAsync({
        planId,
      })

      if (response?.url) {
        window.location.href = response.url
      }
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setLoadingPlang(null)
    }
  }

  const handleDowngradePlan = async (planId: string) => {
    try {
      setLoadingPlang(planId)
      const response = await createPlanUpdatePortalSession.mutateAsync({
        planId,
      })

      if (response?.url) {
        window.location.href = response.url
      }
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setLoadingPlang(null)
    }
  }

  const handleFullPortal = async () => {
    try {
      const response = await createFullPortalSession.mutateAsync()

      if (response?.url) {
        window.location.href = response.url
      }
    } catch (error) {
      toast.error(getErrorMessage(error))
    }
  }

  return (
    <div className="min-h-[calc(100dvh-100px)] bg-gray-50 dark:bg-black">
      <PricingCardsContainer
        error={plansError}
        formatPrice={formatPrice}
        hasPendingCancellation={plansData?.hasPendingCancellation || false}
        isLoading={isPlansLoading || returnedFromPaymentLoading}
        loadingPlanId={loadingPlang}
        plans={plansData?.data || []}
        onCancelSubscription={handleCancelSubscription}
        onDowngradePlan={handleDowngradePlan}
        onResumeSubscription={handleResumeSubscription}
        onRetry={() => refetchPlans()}
        onSelectPlan={handleManageBilling}
        onUpgradePlan={handleUpgradePlan}
      />

      {/* Manage Billing & Subscriptions Section */}
      {!isPlansLoading && !returnedFromPaymentLoading && (
        <div className="max-w-4xl mx-auto mb-12 lg:mb-0 px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-gray-800 dark:border-opacity-50 p-6 shadow dark:shadow-gray-900/50">
            <div className="text-center space-y-6">
              <h2 className="text-3xl font-semibold text-gray-900 dark:text-white">
                Manage Billing & Subscriptions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Access your billing management interface to view invoices, update payment methods,
                and manage your subscription—including upgrades, downgrades, and cancellations.
              </p>
              <Button
                className="bg-blue-600 dark:bg-blue-500 hover:bg-primary dark:hover:bg-blue-600 text-white transition-all duration-300 shadow-lg dark:shadow-blue-500/20"
                endContent={<ArrowRight className="w-4 h-4" />}
                isLoading={createFullPortalSession.isPending}
                radius="sm"
                size="lg"
                onPress={handleFullPortal}
              >
                Go to Billing Portal
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscriptionManagement
